import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import {
  UNDERWRITING_STATUS_CONFIG,
  FundingPolicyType,
  isUnderwritingEnabledForPolicyType,
} from '@shared';
import { Button, Spinner } from '@design-system';
import { ChevronRight } from 'lucide-react';
import { UnderwritingSlideOut } from './underwriting-slide-out';
import { QuestionnaireDialog } from '@/features/client-quick-plan/components/questionnaire-dialog';
import { useUnderwritingStatus } from '../hooks/use-underwriting-status';
import { useToast } from '@/components/hooks/use-toast';

interface UnderwritingStatusBadgeProps {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
  className?: string;
  size?: 'sm' | 'md'; // sm for compact badges (like in FundingTable), md for quote cards (default)
  appearance?: 'badge' | 'quote-card-action';
}

export function UnderwritingStatus({
  productName,
  fundingPolicyType,
  policyType,
  className,
  size = 'md',
  appearance = 'badge',
}: UnderwritingStatusBadgeProps) {
  const { flags } = useFeatureFlags();
  const { toast } = useToast();
  const underwritingEnabled = isUnderwritingEnabledForPolicyType(fundingPolicyType, flags);

  const {
    status,
    underwritingResult,
    structuredResult,
    pipelineMetadata,
    hasUnderwritingResult,
    isSampleClient,
    isLoading,
    showSlideOut,
    setShowSlideOut,
    showQuestionnaireDialog,
    setShowQuestionnaireDialog,
    questionnaireVariant,
    questionnaireClient,
    handleCheckEligibilityClick,
    handleViewDetails,
  } = useUnderwritingStatus({
    productName,
    fundingPolicyType,
    policyType,
  });

  if (flags.nycLifeDemo) return null;

  const isQuoteCardAction = appearance === 'quote-card-action';
  const displayedStatus = underwritingEnabled ? status : undefined;

  // Show CTA button when status doesn't exist
  if (!displayedStatus) {
    // A sample result is loaded from a server-side fixture. Never expose a trigger while that
    // initial request is in flight (or if it fails), because sample underwriting is read-only.
    if (isSampleClient && underwritingEnabled) return null;

    return (
      <>
        <Button
          variant={isQuoteCardAction ? 'outline' : 'ghost'}
          size="sm"
          onClick={
            underwritingEnabled
              ? handleCheckEligibilityClick
              : () => toast({ title: 'AI underwriting for this product is coming soon' })
          }
          disabled={underwritingEnabled && isLoading}
          className={cn(
            isQuoteCardAction
              ? 'border-primary-200 bg-primary/5 text-primary-dark hover:border-primary/20 hover:bg-primary/10 h-8 w-fit rounded-md px-3 text-sm'
              : 'border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 !h-7 w-fit self-start !rounded-full border !px-2 text-xs font-medium',
            className,
          )}
          data-testid="get-underwriting-eligibility-cta"
        >
          {underwritingEnabled && isLoading ? (
            <>
              <Spinner className="h-3 w-3" />
              Checking...
            </>
          ) : (
            <>
              Check Eligibility
              <ChevronRight className="!size-3" />
            </>
          )}
        </Button>

        {/* Questionnaire Dialog */}
        <QuestionnaireDialog
          open={showQuestionnaireDialog}
          onOpenChange={setShowQuestionnaireDialog}
          variant={questionnaireVariant}
          client={questionnaireClient}
        />
      </>
    );
  }

  const config = UNDERWRITING_STATUS_CONFIG[displayedStatus];
  if (!config) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleViewDetails}
        disabled={!hasUnderwritingResult}
        data-testid="quote-detail-eligibility-status"
        className={cn(
          'inline-flex items-center gap-1.5 border font-medium transition-all',
          'hover:shadow-sm disabled:pointer-events-none disabled:opacity-50',
          hasUnderwritingResult && 'cursor-pointer',
          isQuoteCardAction
            ? 'h-8 w-fit justify-center rounded-md px-3 text-sm'
            : cn('h-7 rounded-full px-2 text-xs', size === 'sm' ? 'w-max' : 'w-fit'),
          config.classes.bgColor,
          config.classes.textColor,
          config.classes.borderColor,
          config.classes.hoverBgColor,
          className,
        )}
        aria-label={`View ${config.label} details`}
      >
        {config.label}
        <ChevronRight className="h-3 w-3" />
      </button>

      {/* Slideout for viewing underwriting details */}
      <UnderwritingSlideOut
        open={showSlideOut}
        onClose={() => setShowSlideOut(false)}
        underwritingResult={underwritingResult}
        structuredResult={structuredResult}
        pipelineMetadata={pipelineMetadata}
        productName={productName}
        fundingPolicyType={fundingPolicyType}
      />
    </>
  );
}
